import { Injectable } from '@angular/core';
import { EvidenceStatus } from '../../../models/chat.model';
import { FrontendLoggerService } from '../../frontend-logger.service';
import { ChatGroundingMapperService } from './chat-grounding-mapper.service';

/** Documento fuente persistido del turno (solo el selected_chunk se guarda en recarga). */
export interface MappedSelectedDocument {
  documentId: string;
  filename: string | null;
}

export interface MappedConversation {
  question: string;
  answer: string;
  selectedChunk?: string;
  selectedDocument?: MappedSelectedDocument;
  /** Todos los documentos referenciados por la respuesta (deduplicados). */
  referencedDocuments?: MappedSelectedDocument[];
  /** Slug del rol experto con el que se envio la pregunta (persistido en el turno de usuario). */
  expertRole?: string | null;
  // --- Fase 4 (aditivo, opcional): evidence_status persistido por turno. Stub seguro:
  // permanece undefined mientras el backend no lo persista, sin romper la recarga. ---
  evidenceStatus?: EvidenceStatus;
  createdAt: Date;
  questionId?: string;
  answerId?: string;
}

/** Forma estrecha de la metadata cruda del turno del asistente (sin `any`). */
interface RawTurnSelectedChunkMetadata {
  document_id?: string | null;
  original_filename?: string | null;
}

interface RawTurnReferencedDocument {
  document_id?: string | null;
  original_filename?: string | null;
}

interface RawTurnMetadata {
  selected_chunk?: {
    metadata?: RawTurnSelectedChunkMetadata | null;
  } | null;
  referenced_documents?: RawTurnReferencedDocument[] | null;
  /** Slug del rol experto aplicado al turno (persistido por el backend solo si hubo rol). */
  expert_role?: string | null;
  // --- Fase 4 (aditivo): evidence_status persistido por turno, si el backend lo guarda. ---
  evidence_status?: unknown;
}

interface RawTurnItem {
  metadata?: RawTurnMetadata | string | null;
}

@Injectable({ providedIn: 'root' })
export class ConversationMapperService {
  constructor(
    private readonly frontendLogger: FrontendLoggerService,
    private readonly grounding: ChatGroundingMapperService
  ) {}

  /**
  * Mapea un array de turnos de sesión a una estructura agrupada y ordenada para la UI.
  * Orden: únicamente por created_at (ascendente), respetando roles (usuario/assistant).
  * @param session Array de objetos de la sesión (cualquier orden)
   */
  mapConversation(session: any[]): MappedConversation[] {
    if (!session || session.length === 0) {
      return [];
    }

    this.frontendLogger.info(`[ConversationMapperService] Mapping conversation with session length: ${session.length}`);
    this.frontendLogger.debug(`[ConversationMapperService] First 3 items: ${JSON.stringify(session.slice(0, 3))}`);

    if (!Array.isArray(session)) {
      return [];
    }
    
    // Ordenar estrictamente por fecha de creación ascendente
    const ordered = [...session].sort((a, b) => {
      const ta = new Date(a?.created_at ?? 0).getTime();
      const tb = new Date(b?.created_at ?? 0).getTime();
      return ta - tb;
    });
    
    // Agrupar secuencialmente: cada 'user' inicia un nuevo turno
    const groups: Array<{ user?: any; assistants: any[] }> = [];
    let currentGroup: { user?: any; assistants: any[] } | null = null;
    
    for (const item of ordered) {
      if (item.role === 'user') {
        // Nuevo turno de conversación
        if (currentGroup) {
          groups.push(currentGroup);
        }
        currentGroup = { user: item, assistants: [] };
      } else if (item.role === 'assistant') {
        // Agregar al grupo actual
        if (!currentGroup) {
          // Si no hay grupo actual, crear uno sin usuario (edge case)
          currentGroup = { assistants: [item] };
        } else {
          currentGroup.assistants.push(item);
        }
      }
    }
    
    // Agregar el último grupo si existe
    if (currentGroup) {
      groups.push(currentGroup);
    }
    
    this.frontendLogger.info(`[ConversationMapperService] Total groups created: ${groups.length}`);
    
    // Filtrar solo grupos que tengan usuario
    const validGroups = groups.filter(group => !!group.user);
    
    this.frontendLogger.info(`[ConversationMapperService] Valid groups with user: ${validGroups.length}`);

    const result: MappedConversation[] = validGroups.map(group => {
      const mapped: MappedConversation = {
        question: group.user!.content,
        answer: group.assistants[0]?.content ?? '',
        selectedChunk: group.assistants[1]?.content,
        createdAt: new Date(group.user!.created_at),
        questionId: group.user!.id,
        answerId: group.assistants[0]?.id
      };
      const selectedDocument = this.extractSelectedDocument(group.assistants);
      if (selectedDocument) {
        mapped.selectedDocument = selectedDocument;
      }
      const referencedDocuments = this.extractReferencedDocuments(group.assistants);
      if (referencedDocuments.length) {
        mapped.referencedDocuments = referencedDocuments;
      }
      const expertRole = this.extractExpertRole(group.user);
      if (expertRole) {
        mapped.expertRole = expertRole;
      }
      this.applyGrounding(mapped, group.assistants);
      return mapped;
    });

    this.frontendLogger.info(`[ConversationMapperService] Mapped conversation result: ${result.length} conversations`);
    return result;
  }

  /**
   * Extrae, de forma null-safe, el documento del selected_chunk persistido.
   * Busca entre las filas de asistente del turno la que lleva
   * `metadata.selected_chunk.metadata.document_id`.
   */
  private extractSelectedDocument(assistants: unknown[]): MappedSelectedDocument | undefined {
    for (const assistant of assistants) {
      const metadata = this.parseRawTurnMetadata((assistant as RawTurnItem | null | undefined)?.metadata);
      const selected = metadata?.selected_chunk?.metadata;
      const documentId = selected?.document_id ? String(selected.document_id).trim() : '';
      if (!documentId) {
        continue;
      }
      const filename = selected?.original_filename ? String(selected.original_filename).trim() : '';
      return { documentId, filename: filename || null };
    }
    return undefined;
  }

  /**
   * Extrae, deduplicado por `document_id`, TODOS los documentos referenciados por
   * la respuesta desde `metadata.referenced_documents` (persistido por el backend).
   * Permite ofrecer descarga de todos los documentos al recargar, no solo el
   * seleccionado.
   */
  private extractReferencedDocuments(assistants: unknown[]): MappedSelectedDocument[] {
    const documents: MappedSelectedDocument[] = [];
    const seen = new Set<string>();
    for (const assistant of assistants) {
      const metadata = this.parseRawTurnMetadata((assistant as RawTurnItem | null | undefined)?.metadata);
      const referenced = metadata?.referenced_documents;
      if (!Array.isArray(referenced)) {
        continue;
      }
      for (const entry of referenced) {
        const documentId = entry?.document_id ? String(entry.document_id).trim() : '';
        if (!documentId || seen.has(documentId)) {
          continue;
        }
        seen.add(documentId);
        const filename = entry?.original_filename ? String(entry.original_filename).trim() : '';
        documents.push({ documentId, filename: filename || null });
      }
    }
    return documents;
  }

  /**
   * Extrae el slug del rol experto persistido en el metadata del turno de USUARIO.
   * El backend lo guarda solo cuando hubo rol; se usa para repintar el badge por-mensaje
   * al recargar la sesion. Tolera metadata objeto o string JSON via parseRawTurnMetadata.
   */
  private extractExpertRole(userTurn: unknown): string | null {
    const metadata = this.parseRawTurnMetadata((userTurn as RawTurnItem | null | undefined)?.metadata);
    const value = metadata?.expert_role;
    if (typeof value !== 'string') {
      return null;
    }
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  }

  /**
   * Fase 4 (paridad de recarga): adjunta el grounding persistido por turno si el backend
   * lo guardó en la metadata de alguna fila de asistente. Reutiliza el mismo normalizador
   * defensivo que la query fresca (ChatGroundingMapperService). Stub seguro: si no hay
   * grounding persistido, los campos quedan undefined y la recarga no cambia.
   */
  private applyGrounding(mapped: MappedConversation, assistants: unknown[]): void {
    for (const assistant of assistants) {
      const metadata = this.parseRawTurnMetadata((assistant as RawTurnItem | null | undefined)?.metadata);
      if (!metadata) {
        continue;
      }
      const evidenceStatus = this.grounding.mapEvidenceStatus(metadata.evidence_status);
      if (evidenceStatus && !mapped.evidenceStatus) {
        mapped.evidenceStatus = evidenceStatus;
      }
    }
  }

  /** Lee la metadata cruda del turno, tolerando objeto o string JSON. */
  private parseRawTurnMetadata(metadata: RawTurnMetadata | string | null | undefined): RawTurnMetadata | null {
    if (!metadata) {
      return null;
    }
    if (typeof metadata === 'object') {
      return metadata;
    }
    if (typeof metadata !== 'string') {
      return null;
    }
    try {
      const parsed = JSON.parse(metadata) as unknown;
      return parsed && typeof parsed === 'object' ? (parsed as RawTurnMetadata) : null;
    } catch (error) {
      this.frontendLogger.warn(
        '[ConversationMapperService] No se pudo parsear metadata cruda del turno: ' +
          (error instanceof Error ? error.message : String(error))
      );
      return null;
    }
  }
}
